import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  CaretLeft as ChevronLeft,
  CaretRight as ChevronRight,
  FunnelSimple as Filter,
  CalendarBlank as CalendarIcon,
  X,
  Gavel,
  ChatCircle,
  Briefcase,
  ListChecks,
  ArrowSquareOut,
  Clock,
  MapPin,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useDiaryStore,
  dateKey,
  nextEventId,
  HOURS,
  KIND_LABEL,
  CREATABLE_KINDS,
  KIND_TONE,
  TONE_STYLE,
  type CalEvent,
  type EventKind,
  type EventTone,
  todayKey as getTodayKey,
} from '@/lib/diaryStore';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import { useHearingsStore } from '@/lib/hearingsStore';
import { getStatusConfig } from '@/lib/hearingConstants';
import { Trophy } from '@phosphor-icons/react';
import { ScrollLock } from '@/components/shared/ScrollLock';

const HOUR_PX = 64;

const KIND_ICON: Record<EventKind, React.ElementType> = {
  hearing: Gavel, conference: ChatCircle, task: ListChecks, brief_return: Briefcase, personal: CalendarIcon,
  meeting: Briefcase, travel: MapPin, leave: CalendarIcon,
};

/** TONE_STYLE from the store omits hover; add it here for the calendar blocks. */
const TONE_HOVER: Record<EventTone, string> = {
  brand:   TONE_STYLE.brand   + ' hover:bg-brand-100 dark:hover:bg-brand-900/40',
  amber:   TONE_STYLE.amber   + ' hover:bg-amber-100 dark:bg-amber-500/15 dark:hover:bg-amber-500/20',
  red:     TONE_STYLE.red     + ' hover:bg-red-100 dark:bg-red-500/15 dark:hover:bg-red-500/20',
  emerald: TONE_STYLE.emerald + ' hover:bg-emerald-100 dark:bg-emerald-500/15 dark:hover:bg-emerald-500/20',
  violet:  TONE_STYLE.violet  + ' hover:bg-violet-100 dark:bg-violet-500/15 dark:hover:bg-violet-500/20',
};

export default function CalendarPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const allEvents = useDiaryStore((s) => s.events);
  const storeAddEvent = useDiaryStore((s) => s.addEvent);
  const storeDeleteEvent = useDiaryStore((s) => s.deleteEvent);
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);
  const wsConnected = useWebSocketStore((s) => s.connected);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Polling + WebSocket subscription — re-attaches when socket connects/reconnects.
  useEffect(() => {
    const poll = setInterval(fetchEvents, 300_000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchEvents(); };
    document.addEventListener('visibilitychange', onVisible);
    const unsubWs = onCalendarUpdate(() => fetchEvents());
    return () => { clearInterval(poll); document.removeEventListener('visibilitychange', onVisible); unsubWs(); };
  }, [fetchEvents, wsConnected]);
  const [newEventDraft, setNewEventDraft] = useState<{ day: number; start: number } | null>(null);
  const [selected, setSelected] = useState<CalEvent | null>(null);
  const navigate = useNavigate();

  const today = new Date();
  const days = useMemo<Date[]>(() => {
    const start = new Date();
    const monday = new Date(start);
    const dow = (start.getDay() + 6) % 7;
    monday.setDate(start.getDate() - dow + weekOffset * 7);
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, [weekOffset]);
  const weekStart = days[0] ?? today;

  /** Date keys for the 5 visible weekdays */
  const dayKeys = useMemo(() => days.map(dateKey), [days]);

  /** Store events filtered to this week, with computed dayIdx for grid positioning */
  const weekEvents = useMemo(() => {
    return allEvents
      .map((e) => {
        const idx = dayKeys.indexOf(e.date);
        if (idx === -1) return null;
        return { ...e, dayIdx: idx };
      })
      .filter(Boolean) as (CalEvent & { dayIdx: number })[];
  }, [allEvents, dayKeys]);

  const handleEmptySlotClick = (day: number, hour: number) => {
    setNewEventDraft({ day, start: hour });
  };

  const handleEventClick = (e: CalEvent & { dayIdx: number }) => {
    setSelected(e);
  };

  const addEvent = (e: CalEvent) => {
    storeAddEvent(e);
    setNewEventDraft(null);
  };

  const deleteEvent = (id: string) => {
    storeDeleteEvent(id);
    setSelected(null);
  };

  const todayKey = getTodayKey();
  const todayCount = weekEvents.filter((e) => e.date === todayKey).length;

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Calendar"
        subtitle={`${weekEvents.length} events this week · ${todayCount} today · click any slot to add`}
        actions={
          <>
            {/* No filter UI is implemented; the button beside it (New event) works, so this read
              as live. Disabled rather than removed so the intent stays visible. */}
          <Button variant="outline" size="md" disabled title="Calendar filters aren't available yet." leftIcon={<Filter className="h-4 w-4" />}>Filters</Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setNewEventDraft({ day: 0, start: 0 })}
            >
              New event
            </Button>
          </>
        }
      />

      {/* Toolbar */}
      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)}>Today</Button>
            <button
              onClick={() => setWeekOffset(weekOffset - 1)}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setWeekOffset(weekOffset + 1)}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-base font-semibold text-foreground">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            Week of {weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <div className="ml-auto flex items-center gap-1 rounded-md border border-border bg-card p-0.5 text-xs">
            {/* Only the week view is implemented. Four inert buttons with hover states, one
                highlighted by array INDEX rather than by state, read as a working segmented
                control — clicking any of them did nothing at all. Show what is actually true;
                the other views return here when they exist. */}
            <span className="rounded-sm bg-brand-600 px-3 py-1.5 font-medium text-white">Week</span>
          </div>
        </div>
      </Card>

      {/* Week grid */}
      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-[60px_repeat(5,1fr)] border-b border-border text-xs">
            <div className="border-r border-border bg-muted/30 px-3 py-3" />
            {days.map((d) => {
              const isToday = d.toDateString() === today.toDateString();
              return (
                <div
                  key={d.toISOString()}
                  className={`border-r border-border px-3 py-3 last:border-r-0 ${isToday ? 'bg-brand-50' : 'bg-muted/30'}`}
                >
                  <div className={`text-[10px] font-semibold uppercase tracking-wider ${isToday ? 'text-brand-700 dark:text-brand-300' : 'text-muted-foreground'}`}>
                    {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                  </div>
                  <div className={`tabular mt-0.5 text-base font-semibold ${isToday ? 'text-brand-700 dark:text-brand-300' : 'text-foreground'}`}>
                    {d.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-[60px_repeat(5,1fr)]">
            {/* Hours column */}
            <div className="border-r border-border">
              {HOURS.map((h) => (
                <div key={h} className="flex items-start justify-end border-b border-border px-2 pt-1 text-[10px] text-muted-foreground" style={{ height: HOUR_PX }}>
                  {h}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {days.map((_, dayIdx) => (
              <div key={dayIdx} className="relative border-r border-border last:border-r-0">
                {/* Empty hour cells (clickable to add) */}
                {HOURS.map((_h, hourIdx) => (
                  <button
                    key={hourIdx}
                    type="button"
                    onClick={() => handleEmptySlotClick(dayIdx, hourIdx)}
                    className="block w-full border-b border-border transition hover:bg-brand-50/40 focus:bg-brand-50/40 focus:outline-none"
                    style={{ height: HOUR_PX }}
                    aria-label={`Add event at ${HOURS[hourIdx]}`}
                  />
                ))}

                {/* Event blocks (overlay) */}
                {weekEvents.filter((e) => e.dayIdx === dayIdx).map((e) => {
                  const Icon = KIND_ICON[e.kind];
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        handleEventClick(e);
                      }}
                      className={`absolute inset-x-1 cursor-pointer rounded-md border px-2 py-1.5 text-left text-xs shadow-xs transition hover:shadow-card focus:outline-none focus:ring-2 focus:ring-brand-200 ${
                        // A provisional hold must not read as a booking. MyDiaryPage and the clerk
                        // Master Diary already draw it dashed; this grid did not, so the same event
                        // looked committed on one screen and provisional on another — the worst
                        // possible outcome, because it destroys trust in every block on the page.
                        e.status === 'tentative'
                          ? 'border-dashed border-amber-300 bg-amber-50/50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
                          : TONE_HOVER[e.tone]
                      }`}
                      style={{
                        top: `${e.start * HOUR_PX + 2}px`,
                        height: `${e.span * HOUR_PX - 4}px`,
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <Icon className="h-3 w-3 shrink-0" />
                        <span className="truncate font-semibold">{e.title}</span>
                      </div>
                      {e.status === 'tentative' && (
                        <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide opacity-80">
                          Provisional hold
                        </div>
                      )}
                      {e.sub && <div className="mt-0.5 truncate opacity-80">{e.sub}</div>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-brand-500" /> Court hearing
        </div>
        <div className="flex items-center gap-2">
          {/* Named so the distinction is legible without hovering — a dashed block that is not
              explained is just a block someone will assume is a booking. */}
          <span className="h-2.5 w-2.5 rounded-full border border-dashed border-amber-400 bg-amber-50" />
          Provisional hold (not committed)
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Conference / meeting
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Brief return / deadline
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-violet-500" /> Task
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Personal
        </div>
        <Badge variant="default" className="ml-auto">Click any empty slot to add</Badge>
      </div>

      {/* New event modal */}
      {newEventDraft && (
        <NewEventModal
          draft={newEventDraft}
          days={days}
          onCancel={() => setNewEventDraft(null)}
          onSave={addEvent}
        />
      )}

      {/* Event detail popover */}
      {selected && (
        <EventDetailModal
          event={selected}
          onClose={() => setSelected(null)}
          onDelete={() => deleteEvent(selected.id)}
          onOpenCase={() => {
            navigate(`/admin/matters/${selected.caseId}`);
          }}
          onOpenHearing={() => {
            navigate(`/admin/hearings/${selected.hearingId}`);
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// New event modal
// -----------------------------------------------------------------

interface NewForm {
  title: string;
  kind: EventKind;
  day: number;
  start: number;
  span: number;
  location: string;
  sub: string;
  notes: string;
  caseId: string;
}

function NewEventModal({
  draft,
  days,
  onCancel,
  onSave,
}: {
  draft: { day: number; start: number };
  days: Date[];
  onCancel: () => void;
  onSave: (e: CalEvent) => void;
}) {
  const [form, setForm] = useState<NewForm>({
    title: '',
    kind: 'personal' as EventKind,
    day: draft.day,
    start: draft.start,
    span: 1,
    location: '',
    sub: '',
    notes: '',
    caseId: '',
  });

  const valid = form.title.trim().length > 0;
  const draftDate = days[form.day];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-lg shadow-pop">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <div className="text-base font-semibold text-foreground">New event</div>
            {draftDate && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {draftDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} · {HOURS[form.start]}
              </div>
            )}
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onCancel}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title *</label>
            <input
              type="text"
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="e.g. R v Whitfield — PTPH"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</label>
            <div className="grid grid-cols-4 gap-1.5">
              {CREATABLE_KINDS.map((k) => {
                const Icon = KIND_ICON[k];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k })}
                    className={`flex flex-col items-center gap-1 rounded-md border p-2 transition ${
                      form.kind === k ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${form.kind === k ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'}`} />
                    <span className="text-[10px] font-semibold text-foreground">{KIND_LABEL[k]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Day</label>
              <select
                value={form.day}
                onChange={(e) => setForm({ ...form, day: parseInt(e.target.value, 10) })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                {days.map((d, i) => (
                  <option key={i} value={i}>
                    {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Start</label>
              <select
                value={form.start}
                onChange={(e) => setForm({ ...form, start: parseInt(e.target.value, 10) })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                {HOURS.map((h, i) => (
                  <option key={h} value={i}>{h}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hours</label>
              <select
                value={form.span}
                onChange={(e) => setForm({ ...form, span: parseInt(e.target.value, 10) })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>{n} hr</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Location</label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="e.g. Court 4, Royal Courts of Justice"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Subtitle / counsel</label>
            <input
              type="text"
              value={form.sub}
              onChange={(e) => setForm({ ...form, sub: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="e.g. EWHC ChD · Thornton KC"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="h-20 w-full resize-none rounded-md border border-border bg-card p-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="Optional — anything you want to remember"
            />
          </div>
        </CardContent>

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <div className="text-xs text-muted-foreground">Saves to your diary instantly</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="md" onClick={onCancel}>Cancel</Button>
            <Button
              variant="primary"
              size="md"
              disabled={!valid}
              onClick={() => {
                const eventDate = days[form.day];
                onSave({
                  id: nextEventId(),
                  date: eventDate ? dateKey(eventDate) : getTodayKey(),
                  title: form.title.trim(),
                  sub: form.sub.trim() || undefined,
                  location: form.location.trim() || undefined,
                  notes: form.notes.trim() || undefined,
                  kind: form.kind,
                  tone: KIND_TONE[form.kind],
                  start: form.start,
                  span: form.span,
                  caseId: form.caseId.trim() || undefined,
                  createdBy: 'Admin',
                  status: 'confirmed',
                });
              }}
            >
              Save event
            </Button>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Event detail popover
// -----------------------------------------------------------------

function EventDetailModal({
  event,
  onClose,
  onDelete,
  onOpenCase,
  onOpenHearing,
}: {
  event: CalEvent;
  onClose: () => void;
  onDelete: () => void;
  onOpenCase: () => void;
  onOpenHearing: () => void;
}) {
  const Icon = KIND_ICON[event.kind];
  const day = new Date(event.date + 'T00:00:00');
  const hearing = useHearingsStore((s) =>
    event.hearingId ? s.hearings.find((h) => h.id === event.hearingId) : undefined
  );
  const statusCfg = hearing ? getStatusConfig(hearing.status) : null;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-md shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className={`flex items-start justify-between border-b border-border px-6 py-4 ${TONE_STYLE[event.tone].split(' ').slice(0, 1).join(' ')}`}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-card">
              <Icon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <Badge>{KIND_LABEL[event.kind]}</Badge>
              <div className="mt-1 text-base font-semibold text-foreground">{event.title}</div>
              {event.sub && <div className="mt-0.5 text-xs text-muted-foreground">{event.sub}</div>}
            </div>
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="space-y-3 p-6">
          <div className="flex items-start gap-3">
            <CalendarIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="text-sm">
              <div className="font-medium text-foreground">
                {day.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {HOURS[event.start]} — {HOURS[Math.min(event.start + event.span, HOURS.length - 1)]} ({event.span}h)
              </div>
            </div>
          </div>
          {event.location && (
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="text-sm font-medium text-foreground">{event.location}</div>
            </div>
          )}
          {event.notes && (
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="text-sm text-foreground">{event.notes}</div>
            </div>
          )}

          {statusCfg && (
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusCfg.cls}`}>{statusCfg.label}</span>
              {statusCfg.nextAction && <span className="text-xs text-muted-foreground">→ {statusCfg.nextAction}</span>}
            </div>
          )}
          {hearing?.outcome && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                <Trophy className="h-3 w-3" /> Outcome
              </div>
              <p className="text-sm text-foreground">{hearing.outcome}</p>
            </div>
          )}

          {(event.caseId || event.hearingId) && (
            <div className="space-y-2 border-t border-border pt-3">
              {event.hearingId && (
                <Button variant="outline" size="sm" className="w-full justify-between" onClick={onOpenHearing}>
                  <span>Open hearing workspace</span>
                  <ArrowSquareOut className="h-3.5 w-3.5" />
                </Button>
              )}
              {event.caseId && (
                <Button variant="outline" size="sm" className="w-full justify-between" onClick={onOpenCase}>
                  <span>Open matter file</span>
                  <ArrowSquareOut className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </CardContent>

        <div className="flex items-center justify-between border-t border-border px-6 py-3">
          <button onClick={onDelete} className="inline-flex items-center gap-1.5 text-xs font-medium text-danger hover:underline">
            <Trash className="h-3.5 w-3.5" />
            Delete
          </button>
          <div className="flex items-center gap-2">
            {/* No edit flow exists for a calendar event yet. Rendering a dead Edit between a
                  working Delete and a working Close made it read as live; the only way to change
                  an event was delete-and-recreate, with no hint of that. */}
              <Button
                variant="outline"
                size="sm"
                disabled
                title="Editing an event isn't available yet — delete it and create it again."
                leftIcon={<PencilSimple className="h-3.5 w-3.5" />}
              >Edit</Button>
            <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
}
